import { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '@practium/ui';
import { usePostBindingUser, useUpdateBindingUser } from '@practium/features/api';
import type { FarmerFormData } from '../types';
import { FarmerSchema } from '../schemas/farmer.schema';
import { FARMER_FIELD_LABELS, DEFAULT_FARMER_FORM } from '../constants/farmer.constants';
import { applyPhoneMask, formatPhoneForApi, getDocumentType } from '../utils/masks';

export interface UseFarmerFormOptions {
	initialData?: FarmerFormData;
	onSubmit: () => void;
}

export const useFarmerForm = ({ initialData, onSubmit }: UseFarmerFormOptions) => {
	const toast = useToast();
	const [searchParams] = useSearchParams();
	const routeParams = useParams<{ id?: string }>();
	const bindingUserId = searchParams.get('id') ?? routeParams.id;
	const { mutateAsync: createBindingUser } = usePostBindingUser({
		bindingType: 1,
	});
	const { mutateAsync: updateBindingUser } = useUpdateBindingUser();
	const initialForm = initialData
		? {
			...DEFAULT_FARMER_FORM,
			...initialData,
			phoneNumber: applyPhoneMask(initialData.phoneNumber),
		}
		: { ...DEFAULT_FARMER_FORM };
	const [form, setForm] = useState<FarmerFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const set = (key: keyof FarmerFormData, value: string | boolean) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = FarmerSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => FARMER_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			const payload = {
				name: form.name,
				surName: form.surName,
				phoneNumber: formatPhoneForApi(form.phoneNumber) || undefined,
				email: form.email || undefined,
				document: form.document || undefined,
				documentType: getDocumentType(form.document),
				status: form.status === 'true',
				canConnect: false as const,
			};

			if (bindingUserId) {
				await updateBindingUser({
					bindingType: 1,
					id: bindingUserId,
					data: payload,
				});
			} else {
				await createBindingUser(payload);
			}

			toast.success('Agricultor salvo com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar agricultor');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty };
};
